import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { formatoNumero } from '@/lib/utils/formato-precio';
import type { StrategyMode, StrategyModeLeido } from '../types/strategy-applications.types';

/**
 * El permiso de una estrategia (maqueta S5): tres opciones. La del medio es AUTO
 * con umbral (`askAboveChangePct`, plan 4A: > 0 y ≤ 100; la pantalla pide 1–100).
 * Sin precio posible pregunta siempre, elija lo que elija.
 */

export interface Permiso {
  mode: StrategyMode;
  askAboveChangePct: number | null;
}

export const PERMISO_INICIAL: Permiso = { mode: 'SUPERVISED', askAboveChangePct: null };

/** «Aplica sola, pregunta si supera 10 %». `AUTO_WITH_RULES` (legado) se lee como AUTO. */
export function permisoEnPalabras(
  mode: StrategyModeLeido,
  askAboveChangePct: number | null
): string {
  if (mode === 'SUPERVISED') return 'Siempre te pregunta';
  return askAboveChangePct == null
    ? 'Aplica sola'
    : `Aplica sola, pregunta si el cambio supera ${formatoNumero(askAboveChangePct)} %`;
}

/** El umbral escrito, si sirve: entre 1 y 100. */
export function umbralValido(texto: string): number | null {
  const n = Number(texto.replace(',', '.'));
  return texto.trim() !== '' && Number.isFinite(n) && n >= 1 && n <= 100 ? n : null;
}

type Opcion = 'SOLA' | 'UMBRAL' | 'SIEMPRE';
const opcionDe = (p: Permiso): Opcion =>
  p.mode === 'SUPERVISED' ? 'SIEMPRE' : p.askAboveChangePct == null ? 'SOLA' : 'UMBRAL';

/**
 * `onChange(null)` = el umbral escrito no sirve: quien lo usa no deja guardar.
 * El estado de las opciones es propio: `value` sólo da el punto de partida.
 */
export function PermisoDeEstrategia({
  value,
  onChange,
  enColumnas = false,
}: {
  value: Permiso;
  onChange: (p: Permiso | null) => void;
  /** En el asistente, las tres opciones lado a lado (maqueta del pulido). */
  enColumnas?: boolean;
}) {
  const [opcion, setOpcion] = useState<Opcion>(opcionDe(value));
  const [texto, setTexto] = useState(
    value.askAboveChangePct == null ? '10' : String(value.askAboveChangePct)
  );
  const umbral = umbralValido(texto);

  const elegir = (o: Opcion) => {
    setOpcion(o);
    if (o === 'SIEMPRE') onChange({ mode: 'SUPERVISED', askAboveChangePct: null });
    else if (o === 'SOLA') onChange({ mode: 'AUTO', askAboveChangePct: null });
    else onChange(umbral == null ? null : { mode: 'AUTO', askAboveChangePct: umbral });
  };

  const caja = (activa: boolean) =>
    cn(
      'flex cursor-pointer items-start gap-2 rounded-md border p-3 text-sm',
      activa && 'border-primary bg-primary/5'
    );

  return (
    <div className="space-y-2">
      <div className="text-sm font-medium">Cuando propone un precio…</div>
      <div
        role="radiogroup"
        aria-label="Cuando propone un precio"
        className={enColumnas ? 'grid grid-cols-3 gap-2' : 'space-y-2'}
      >
        <label className={caja(opcion === 'SOLA')}>
          <input
            type="radio"
            name="permiso"
            aria-label="Aplica sola"
            checked={opcion === 'SOLA'}
            onChange={() => elegir('SOLA')}
          />
          <span>
            <span className="block font-medium">Aplica sola</span>
            <span className="text-xs text-muted-foreground">Siempre dentro de las reglas.</span>
          </span>
        </label>
        <label className={caja(opcion === 'UMBRAL')}>
          <input
            type="radio"
            name="permiso"
            aria-label="Aplica sola, pero preguntame si el cambio supera un %"
            checked={opcion === 'UMBRAL'}
            onChange={() => elegir('UMBRAL')}
          />
          <span>
            <span className="block font-medium">
              Aplica sola, pero preguntame si el cambio supera{' '}
              <span className="inline-flex items-center gap-1 whitespace-nowrap">
                <Input
                  aria-label="Umbral en %"
                  inputMode="decimal"
                  className="h-8 w-16"
                  value={texto}
                  onFocus={() => opcion !== 'UMBRAL' && elegir('UMBRAL')}
                  onChange={(e) => {
                    setTexto(e.target.value);
                    setOpcion('UMBRAL');
                    const n = umbralValido(e.target.value);
                    onChange(n == null ? null : { mode: 'AUTO', askAboveChangePct: n });
                  }}
                />
                %
              </span>
            </span>
            <span className="block text-xs text-muted-foreground">
              Lo que se mueve poco se aplica solo; un cambio grande te espera.
            </span>
            {opcion === 'UMBRAL' && umbral == null && (
              <span role="alert" className="block text-xs text-destructive">
                El umbral va de 1 a 100 %.
              </span>
            )}
          </span>
        </label>
        <label className={caja(opcion === 'SIEMPRE')}>
          <input
            type="radio"
            name="permiso"
            aria-label="Siempre preguntame"
            checked={opcion === 'SIEMPRE'}
            onChange={() => elegir('SIEMPRE')}
          />
          <span>
            <span className="block font-medium">Siempre preguntame</span>
            <span className="text-xs text-muted-foreground">Ningún precio se mueve sin tu sí.</span>
          </span>
        </label>
      </div>
      <p className="text-xs text-muted-foreground">
        Si las reglas no se pueden cumplir todas (un conflicto entre reglas), te pregunta siempre.
      </p>
      {opcion === 'SOLA' && (
        <p className="text-xs text-alerta">
          Los precios van a cambiar sin que nadie los apruebe. Conviene empezar con «siempre
          preguntame» y mirar unos días qué propone.
        </p>
      )}
    </div>
  );
}
